import { useId, useLayoutEffect, useRef } from "react";
import type { ReactElement } from "react";
import type { LiquiditySeries, Scenario, Unternehmen } from "@kundeninfo/api-client";
import { Icon, useSofortPopup } from "@kundeninfo/ui";
import { Auswahlmenue, Chip, Knopf, Menue, Optionen, Werkzeuggruppe, Werkzeugleiste } from "@kundeninfo/ui/me-design";
import { KiKnopf } from "../../components/meHuellen.js";
import { UnternehmensChips } from "../../components/Filterkopf.js";
import { GRANULARITIES, shiftWindow } from "./liquidityModel.js";
import type { DetailWindow } from "./liquidityModel.js";
import { LIQUIDITY_TEXT_WEB as T } from "./liquidityTexts.js";
import "./Liquidity.css";

type Granularity = LiquiditySeries["granularity"];

/** Feste Breite des Auslösers: Sonst kippt die Leiste mit dem Text (`me-auswahlmenue`). Gemessen: „je Unternehmen“ passt, und bei 1280 px bleibt
 *  die Leiste in Stufe 0 (Bericht Aufgabe 6, Fixrunde 1); „Basis je Unternehmen“ passt erst ab 14em, und dann kippt sie bei 1280 px. */
const SELECT_WIDTH = "11.5em";
const SCENARIO_ACTIONS = ["create", "duplicate", "edit", "baseline", "delete"] as const;
export type ScenarioAction = (typeof SCENARIO_ACTIONS)[number];
const DRAFT_ACTIONS = ["save", "saveAsNew", "discard"] as const;
type DraftAction = (typeof DRAFT_ACTIONS)[number];

/** Das Element kennt seine Optionen erst nach dem nächsten Bild: Mit neuen Optionen wird es neu gezogen, die Wahl allein lässt es (und den Fokus) stehen. */
const optionsKey = (list: readonly Scenario[]): string => list.map((c) => c.id).join();

export type LiquidityToolbarProps = {
  series: LiquiditySeries | null; scenarios: readonly Scenario[]; granularity: Granularity; onGranularity: (g: Granularity) => void;
  selectedScenarioId: string | null; compareId: string | null; onScenario: (id: string) => void; onCompare: (id: string | null) => void;
  onScenarioAction: (a: ScenarioAction) => void;
  draftCount: number; previewLimit: number | null; onDraftAction: (a: DraftAction) => void;
  companies: readonly Unternehmen[]; activeCompanyId: string | null; extraCompanies: readonly string[]; onExtraCompanies: (ids: string[]) => void;
  window: DetailWindow; onWindow: (w: DetailWindow) => void; frameMode: boolean; onFrameMode: (on: boolean) => void;
  phone: boolean; onShowPositions: () => void;
};

/**
 * DIE WERKZEUGLEISTE DER LIQUIDITÄT (Spec „Werkzeugleiste“) und darunter die Zeile mit Verdichtung, Zeitraum (Ruling 548) und Entwurf. Die
 * Verdichtung steht dort wie die Stufen der Zeitachse: In der Leiste legte das Element sie bei 1440 px ins „…“ und bei 1280 px auch die
 * Filter hinter „Filter“ (Sichtprobe). Die Leiste kippt nicht: Beide Menüs haben eine feste Breite, „Basis“ und „frei“ behalten ihren Platz
 * (`visibility`), der Entwurf steht auch leer da (`liq__draft`). Bei mehr als einem Unternehmen sagen Szenario und Vergleich „je
 * Unternehmen“, das Sofort-Popup den ganzen Satz „Basis je Unternehmen“ (593, Ruling M2). Am Telefon stehen Unternehmen und Szenario hinter „Filter“ (Stufe 3 des Elements),
 * „Positionen (n)“ öffnet die Spalte, der Entwurf ist ein Symbolknopf mit Zähler und Menü.
 *
 * Wird der Entwurf leer, verschwindet der Knopf, auf dem der Fokus stand: Er geht ohne Rollen an das Szenario-Menü (Telefon: „Positionen (n)“). Während des Schreibens bleiben die
 * Knöpfe bedienbar und tun nichts (`useLiquidityArea`): Ein abgeschalteter Knopf verlöre den Fokus (gemessen, Chromium).
 */
export function LiquidityToolbar(p: LiquidityToolbarProps): ReactElement {
  const menuTrigger = useId();
  const draftTrigger = useId();
  const positionsButton = useRef<HTMLElement | null>(null);
  const draftBox = useRef<HTMLElement | null>(null);
  const menuPopup = useSofortPopup<HTMLElement>(T.toolbar.scenarioMenu);
  const limitPopup = useSofortPopup<HTMLElement>(p.previewLimit === null ? "" : T.draft.previewLimit(p.previewLimit));
  const s = p.series;
  const several = s !== null && s.companyIds.length > 1;
  /** An einer Hülle: `me-auswahlmenue` belegt sein eigenes Popup beim Zeigen mit dem gekürzten Text. Leer spricht die Gruppe mit ihrem Namen. */
  const perCompanyPopup = useSofortPopup<HTMLElement>(several ? T.toolbar.baselinePerCompany : "");
  /** Das Popup hört niemand, und am Telefon gibt es keins: Die Gruppe sagt den vollen Satz an (Fixrunde Aufgabe 6, gemessen in Aufgabe 12). */
  const perCompanyGroup = several ? { role: "group", "aria-label": T.toolbar.baselinePerCompany } : {};
  /** Das gewählte Szenario der Reihe; bei mehreren Unternehmen und ohne Basis trägt sie keine Kennung (593, P6): Dann gibt es nichts zu bearbeiten. */
  const current = p.scenarios.find((c) => c.id === s?.scenario.id);
  const others = several ? [] : p.scenarios.filter((c) => c.id !== p.selectedScenarioId);
  const fiveYears = p.granularity === "FIVE_YEARS";
  const windowed = !fiveYears && p.window !== null;
  const shift = (dir: -1 | 1): void => { if (s !== null) p.onWindow(shiftWindow(s, p.window, dir)); };
  const scenarioPlaceholder = several ? T.toolbar.baselinePerCompanyShort : T.toolbar.baseline;
  const noDraft = p.draftCount === 0;

  /** Stand der Fokus beim letzten Griff in den Entwurf dort? `me-dialog` gibt ihn sonst an einen Knopf zurück, den es nicht mehr gibt. */
  const focusInDraft = useRef(false);
  useLayoutEffect(() => {
    if (!noDraft || !focusInDraft.current) return;
    focusInDraft.current = false;
    const active = document.activeElement;
    if (active !== null && active !== document.body && draftBox.current?.contains(active) !== true) return;
    (p.phone ? positionsButton.current : document.getElementById(menuTrigger))?.focus({ preventScroll: true });
  }, [noDraft, p.phone, menuTrigger]);
  const draftAction = (a: DraftAction): void => {
    focusInDraft.current = draftBox.current?.contains(document.activeElement) === true;
    p.onDraftAction(a);
  };

  const draftBadge = <Chip {...(p.previewLimit === null ? {} : { statusArt: "warnung" as const })}>{T.draft.badge(p.draftCount)}</Chip>;
  const phoneDraft = (
    <span ref={draftBox} className="liq__draft-live" aria-live="polite">
      <span className="liq__draft-phone" hidden={noDraft}>
        <Knopf id={draftTrigger} art="primaer" kompakt aria-label={T.draft.phoneName(p.draftCount)}>
          <span className="liq__draft-mark"><Icon name="pen" size={12} />{p.draftCount}</span>
        </Knopf>
        <Menue fuer={draftTrigger} onGewaehlt={(d) => { const a = DRAFT_ACTIONS.find((x) => x === d.wert); if (a !== undefined) draftAction(a); }}>
          {DRAFT_ACTIONS.map((a) => <option key={a} value={a}>{T.draft[a]}</option>)}
        </Menue>
      </span>
    </span>
  );

  return (
    <div className="liq__toolbar">
      <Werkzeugleiste className="liq__bar" aria-label={T.toolbar.label} aktiv={p.extraCompanies.length > 0 ? 1 : 0} zuruecksetzen="symbol"
        onZurueckgesetzt={() => p.onExtraCompanies([])}
        filter={(
          <>
            {/* Mit einem Unternehmen zeichnet `UnternehmensChips` nichts; im Filterblatt stünde sonst die leere Gruppe. */}
            {p.companies.length > 1 && (
              <Werkzeuggruppe beschriftung={T.toolbar.companies} aktiv={p.extraCompanies.length > 0}>
                <UnternehmensChips label={T.toolbar.companies} companies={p.companies} aktivId={p.activeCompanyId} zusatz={p.extraCompanies}
                  onZusatz={p.onExtraCompanies} />
              </Werkzeuggruppe>
            )}
            <Werkzeuggruppe beschriftung={T.toolbar.scenario}>
              <span ref={perCompanyPopup} className="liq__menu" {...perCompanyGroup}>
                <Auswahlmenue key={several ? "" : optionsKey(p.scenarios)} kompakt beschriftung={T.toolbar.scenario} breite={SELECT_WIDTH} gesperrt={several} platzhalter={scenarioPlaceholder}
                  {...(several ? {} : { wert: p.selectedScenarioId ?? "" })} onGeaendert={(d) => p.onScenario(d.wert)}>
                  {!several && (p.scenarios.length === 0
                    ? <option value="" data-name={T.toolbar.baseline}>{T.toolbar.baseline}</option>
                    : p.scenarios.map((c) => (
                      <option key={c.id} value={c.id} data-name={c.name} {...(c.isBaseline && c.name !== T.toolbar.baseline ? { "data-zusatz": T.toolbar.baseline } : {})}>{c.name}</option>
                    )))}
                </Auswahlmenue>
              </span>
              <span className="liq__baseline" hidden={s === null || several || !s.scenario.isBaseline}><Chip>{T.toolbar.baseline}</Chip></span>
              <Knopf ref={menuPopup} id={menuTrigger} art="leise" kompakt aria-label={T.toolbar.scenarioMenu}>{T.toolbar.scenarioMenuMark}</Knopf>
              <Menue fuer={menuTrigger} onGewaehlt={(d) => { const a = SCENARIO_ACTIONS.find((x) => x === d.wert); if (a !== undefined) p.onScenarioAction(a); }}>
                <option value="create" disabled={s === null || several}>{T.scenario.menu.create}</option>
                <option value="duplicate" disabled={current === undefined}>{T.scenario.menu.duplicate}</option>
                <option value="edit" disabled={current === undefined}>{T.scenario.menu.edit}</option>
                <option value="baseline" disabled={current === undefined || current.isBaseline}>{T.scenario.menu.baseline}</option>
                <option value="delete" data-art="gefahr" disabled={current === undefined || current.isBaseline}>{T.scenario.menu.delete}</option>
              </Menue>
            </Werkzeuggruppe>
            <Werkzeuggruppe beschriftung={T.toolbar.compare}>
              <span ref={perCompanyPopup} className="liq__menu" {...perCompanyGroup}>
                <Auswahlmenue key={several ? "" : optionsKey(others)} kompakt gesperrt={several || others.length === 0} beschriftung={T.toolbar.compare} breite={SELECT_WIDTH}
                  platzhalter={several ? T.toolbar.baselinePerCompanyShort : T.toolbar.compareNone} {...(several ? {} : { wert: p.compareId ?? "" })}
                  onGeaendert={(d) => p.onCompare(d.wert === "" ? null : d.wert)}>
                  {!several && <option value="" data-name={T.toolbar.compareNone}>{T.toolbar.compareNone}</option>}
                  {others.map((c) => <option key={c.id} value={c.id} data-name={c.name}>{c.name}</option>)}
                </Auswahlmenue>
              </span>
            </Werkzeuggruppe>
          </>
        )}
        {...(p.phone ? { haupt: <><KiKnopf knopfRef={positionsButton} onClick={p.onShowPositions}>{T.toolbar.positions(s?.positions.length ?? 0)}</KiKnopf>{phoneDraft}</> } : {})} />
      <div className="liq__steering">
        <Optionen aria-label={T.toolbar.granularity} darstellung="segmente" richtung="waagerecht" wert={p.granularity}
          onGeaendert={(d) => { const g = GRANULARITIES.find((x) => x === d.wert); if (g !== undefined) p.onGranularity(g); }}>
          {GRANULARITIES.map((g) => <option key={g} value={g}>{T.granularity[g]}</option>)}
        </Optionen>
        <div className="liq__range" role="group" aria-label={T.range.label}>
          <KiKnopf kompakt deaktiviert={!windowed} onClick={() => p.onWindow(null)}>{T.range.all}</KiKnopf>
          <KiKnopf kompakt name={T.range.earlier} deaktiviert={!windowed} onClick={() => shift(-1)}><Icon name="chevron-left" size={12} /></KiKnopf>
          <KiKnopf kompakt name={T.range.later} deaktiviert={!windowed} onClick={() => shift(1)}><Icon name="chevron-right" size={12} /></KiKnopf>
          {!p.phone && (
            <Knopf art="sekundaer" gestalt="pille" gedrueckt={p.frameMode} deaktiviert={fiveYears} onClick={() => p.onFrameMode(!p.frameMode)}>
              {T.range.frame}
            </Knopf>
          )}
          <span className="liq__free" hidden={!windowed}><Chip>{T.range.free}</Chip></span>
        </div>
        {!p.phone && (
          <div ref={(n) => { draftBox.current = n; }} className="liq__draft">
            <span ref={limitPopup} className="liq__draft-count" aria-live="polite">{!noDraft && draftBadge}</span>
            <span className="liq__draft-actions" hidden={noDraft}>
              <KiKnopf art="primaer" kompakt onClick={() => draftAction("save")}>{T.draft.save}</KiKnopf>
              <KiKnopf kompakt onClick={() => draftAction("saveAsNew")}>{T.draft.saveAsNew}</KiKnopf>
              <KiKnopf art="leise" kompakt onClick={() => draftAction("discard")}>{T.draft.discard}</KiKnopf>
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
